import { Base } from "../framework/common/base";
import { addAlert } from "../framework/redux/slices/appSlice";
import { store } from "../framework/redux/store";
import { Session } from "../framework/session/session";
import { AlertSeverity, UserPermissionScope } from "../models/common/enums";
import { clearSession } from "../framework/redux/slices/sessionSlice";
import { SerializedError } from "@reduxjs/toolkit";

export interface IApiSuccess {
    message: string;
    errorMessage: string;
}

export interface IApiError {
    code: string;
    message: string;
}

export class ApiSuccess implements IApiSuccess {
    message: string;
    errorMessage: string;

    constructor();
    constructor(obj: IApiSuccess);
    constructor(obj?: any) {
        this.message = obj?.message || "";
        this.errorMessage = obj?.errorMessage || "";
    }
}

export interface ISaveSuccess {
    message: string;
    errorMessage: string;
    id: string;
    rowId: string;
}

export class SaveSuccess implements ISaveSuccess {
    message: string;
    errorMessage: string;
    id: string;
    rowId: string;

    constructor();
    constructor(obj: ISaveSuccess);
    constructor(obj?: any) {
        this.message = obj?.message || "";
        this.errorMessage = obj?.errorMessage || "";
        this.id = obj?.id || "";
        this.rowId = obj?.rowId || "";
    }
}

export const handleApiError = (e: SerializedError) => {
    if (e.name !== "AbortError") {
        if (e.code === "401") { // This check might be unnecessary, since refresh token API is handled separately
            store.dispatch(clearSession());
        }
        if (e.message) {
            store.dispatch(
                addAlert({
                    message: e.message,
                    severity: AlertSeverity.Error,
                    debounce: true,
                })
            );
        }
    }
};

interface EasyOppApiError {
    errors: unknown[];
    title: string;
    status: number;
    traceId: string;
    type: string;
}

// TODO make sure EasyOpp.Api requests use this instead of handleApiError
export const handleEasyOppApiError = (e: EasyOppApiError) => {
    if (e.title) {
        store.dispatch(
            addAlert({
                message: e.title,
                severity: AlertSeverity.Error,
                debounce: true,
            })
        );
    }
};

const getErrorMessageFromBody = (text: string): string => {
    if (Base.isJsonString(text)) {
        const obj = JSON.parse(text);
        const message = obj.message
            ? obj.message
            : obj.Message
                ? obj.Message
                : text;
        return message;
    } else {
        return text;
    }
};

export const handleResponse = (response: Response): any => {
    return new Promise<void>((resolve, reject) => {
        if (response.ok) {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                response.json().then(resolve).catch(console.error);
            } else {
                resolve();
            }
        } else {
            const error = {} as SerializedError;
            error.code = response.status.toString();
            response.text().then((text) => {
                error.message = getErrorMessageFromBody(text);
                reject(error);
            }).catch(console.error);
        }
    });
};

export const handleError = (error) => Promise.reject(error && error.message);
export const authorizationHeaderValue = (): Promise<string> =>
    Session.getAccessToken().then((i) => (i ? `Bearer ${i}` : ""));

export const authorizationHeaderRefreshTokenValue = (): Promise<string> =>
    Session.getRefreshToken().then((i) => (i ? `Bearer ${i}` : ""));

export const getJsonHeaders = (body?: any) =>
    authorizationHeaderValue().then((i) => {
        return {
            Accept: "application/json",
            "Content-Type": "application/json;charset=utf-8",
            "Content-Length": (body ? JSON.stringify(body).length : 0).toString(
                10
            ),
            Authorization: i,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Headers":
                "Origin, X-Requested-With, Content-Type, Accept",
            Scope: UserPermissionScope.Mobile.toString(),
        };
    });
